import { useCallback, useState } from "react";
import type { OrderFormData, OrderResponse } from "@/schemas/order.schema";
import { getCheckoutAttribution, trackCheckoutError } from "@/utils/tracking";

export const ORDER_RETRY_MESSAGE = "Не удалось создать заказ. Попробуйте ещё раз через минуту.";
export const ORDER_SALES_CLOSED_MESSAGE = "Продажи по этому предложению завершены.";

export type OrderCheckoutFailureKind = "retryable" | "sales-closed";

export class OrderCheckoutError extends Error {
  constructor(
    readonly kind: OrderCheckoutFailureKind,
    message: string,
  ) {
    super(message);
    this.name = "OrderCheckoutError";
  }
}

export function asOrderCheckoutError(error: unknown): OrderCheckoutError {
  return error instanceof OrderCheckoutError
    ? error
    : new OrderCheckoutError("retryable", ORDER_RETRY_MESSAGE);
}

/**
 * Shared order-submit core. POSTs the order to `/api/order` with attribution and
 * returns the payment URL (throws on any failure). Single source of truth for the
 * checkout request used by both the site-wide `CheckoutDrawer` (via `CheckoutContext`)
 * and the inline offer-modal form (`useOrderCheckout`).
 */
export async function createOrder(offerId: number, formData: OrderFormData): Promise<string> {
  let response: Response;
  try {
    response = await fetch("/api/order", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...formData, offerId, ...getCheckoutAttribution() }),
    });
  } catch {
    throw new OrderCheckoutError("retryable", ORDER_RETRY_MESSAGE);
  }

  let data: OrderResponse | null = null;
  try {
    data = (await response.json()) as OrderResponse;
  } catch {
    data = null;
  }

  if (response.status === 410) {
    throw new OrderCheckoutError("sales-closed", ORDER_SALES_CLOSED_MESSAGE);
  }

  if (!response.ok || !data?.success) {
    throw new OrderCheckoutError("retryable", ORDER_RETRY_MESSAGE);
  }
  if (!data.paymentUrl) {
    throw new OrderCheckoutError("retryable", ORDER_RETRY_MESSAGE);
  }
  return data.paymentUrl;
}

export type OrderCheckoutState = "idle" | "submitting" | "success" | "error" | "sales-closed";

/**
 * Local checkout state machine for an inline (non-drawer) order form — e.g. a form
 * embedded directly in an offer modal. Deliberately NOT wired to `CheckoutContext`
 * so it does not trigger the global `CheckoutDrawer`. On success it redirects to the
 * payment URL after a short delay (mirrors the drawer behaviour).
 */
export function useOrderCheckout(productTitle: string) {
  const [state, setState] = useState<OrderCheckoutState>("idle");
  const [error, setError] = useState("");
  const [paymentUrl, setPaymentUrl] = useState("");

  const submit = useCallback(
    async (offerId: number, formData: OrderFormData) => {
      setState("submitting");
      setError("");
      try {
        const url = await createOrder(offerId, formData);
        setPaymentUrl(url);
        setState("success");
        setTimeout(() => {
          window.location.href = url;
        }, 2000);
      } catch (err) {
        const failure = asOrderCheckoutError(err);
        setError(failure.message);
        setState(failure.kind === "sales-closed" ? "sales-closed" : "error");
        trackCheckoutError(productTitle, failure.message);
      }
    },
    [productTitle],
  );

  return { state, error, paymentUrl, submit };
}
